<template>
  <div
    class="fts-device"
    :class="{'device--web': device === Web}"
  >
    <!-- The preview app is served from its own origin while the API it calls may sit on a private or
         loopback address, which Chrome gates behind Local Network Access; allow= delegates that permission
         to the frame, otherwise the app's API calls are denied without ever prompting (SS05890). -->
    <div
      class="device-web-container"
      ref="device-web-container"
      key="device-web"
      v-show="device == Web"
    >
      <iframe
        :width="deviceWidth"
        :height="deviceHeight"
        frameborder="0"
        :src="webUrl"
        allow="local-network-access; local-network; loopback-network"
        class="device-web-content"
        ref="device-web-content"
      ></iframe>
    </div>

    <div
      class="device-app-container"
      ref="device-app-container"
      :style="appContainerStyle"
      key="device-app"
      v-show="device != Web"
    >
      <iframe
        :width="deviceWidth"
        :height="deviceHeight"
        frameborder="0"
        :src="appUrl"
        allow="local-network-access; local-network; loopback-network"
        class="device-app-content"
        :class="{'device-app-content--app': device == PhonePortrait || device == PhoneLandscape}"
        ref="device-app-content"
      ></iframe>

      <!-- SS06049: the bezel's box comes from DEVICE_SIZES, never from its image. The four bezels
           used to share one reused <img> (an unkeyed v-if chain) whose src was swapped; a browser
           keeps drawing the previous image, at the previous image's size, until the new SVG has
           loaded (the iPad ones are ~218 KB), and this container sizes itself to that <img> - so
           for several frames the old device's box sat around the new device's iframe. Explicit
           width/height commit the box in the same render as the iframe and the scale; the key
           gives each device its own element, so old artwork is never stretched into a new box. -->
      <img
        v-if="bezel"
        :key="device"
        :src="bezel.src"
        :alt="bezel.alt"
        :width="deviceSize.frameWidth"
        :height="deviceSize.frameHeight"
        class="device-app-frame"
      >
    </div>
  </div>
</template>

<script>
import iphone from '../../assets/device/iphone.svg';
import iphoneLandscape from '../../assets/device/iphone-landscape.svg';
import ipad from '../../assets/device/ipad-10.svg';
import ipadLandscape from '../../assets/device/ipad-10-landscape.svg';


/* Viewport of each simulated device, and the size of the bezel artwork drawn around it. Held as
   plain data so the scale that fits a device onto the stage is a pure function of (device, stage
   size) and can therefore be resolved during render -- see appContainerStyle. */
const DEVICE_SIZES = {
  phonePortrait:  { width: 430,  height: 932,  frameWidth: 478,  frameHeight: 970 },
  phoneLandscape: { width: 932,  height: 430,  frameWidth: 970,  frameHeight: 478 },
  padPortrait:    { width: 820,  height: 1180, frameWidth: 936,  frameHeight: 1302 },
  padLandscape:   { width: 1180, height: 820,  frameWidth: 1302, frameHeight: 936 }
};

export default {
  props: {
    device: {
      type: String,
      default: "phonePortrait"
    },
    webUrl: String,
    appUrl: String
  },
  data: () => ({
    iphone: iphone,
    iphoneLandscape: iphoneLandscape,
    ipad: ipad,
    ipadLandscape: ipadLandscape,
    /* the stage this component fills. It follows the window/panel, never the selected device. */
    stageWidth: 0,
    stageHeight: 0,
    resizeObserver: null
  }),
  computed: {
    deviceSize() {
      return DEVICE_SIZES[this.device] || null;
    },
    deviceWidth() {
      return this.device === this.Web ? this.stageWidth : this.deviceSize && this.deviceSize.width;
    },
    deviceHeight() {
      return this.device === this.Web ? this.stageHeight : this.deviceSize && this.deviceSize.height;
    },
    bezel() {
      const bezels = {
        [this.PhonePortrait]: { src: this.iphone, alt: "iphone" },
        [this.PhoneLandscape]: { src: this.iphoneLandscape, alt: "iphone-landscape" },
        [this.PadPortrait]: { src: this.ipad, alt: "ipad" },
        [this.PadLandscape]: { src: this.ipadLandscape, alt: "ipad-landscape" }
      };
      return (this.deviceSize && bezels[this.device]) || null;
    },
    /* SS06049: the scale used to be written straight onto the element from inside a $nextTick,
       after calcDeviceSize had measured the stage. Switching device re-rendered the iframe at its
       new size and swapped the bezel image immediately, but the matching scale only landed after
       that render -- so for a frame the incoming device was drawn at the outgoing device's scale
       and spilled outside its frame. The stage does not resize when the device changes, so the
       scale is knowable during render: bind it, and all three change together. */
    appContainerStyle() {
      if (this.device === this.Web || !this.deviceSize || !this.stageWidth || !this.stageHeight) {
        return {};
      }

      // fit the bezel onto the stage, shrinking only -- never magnify a device past life size
      const scale = Math.min(
        this.stageHeight / this.deviceSize.frameHeight,
        this.stageWidth / this.deviceSize.frameWidth
      );

      return scale < 1 ? { transform: `scale(${scale})` } : {};
    }
  },
  methods: {
    measureStage() {
      if (!this.$el) return;
      this.stageWidth = this.$el.clientWidth;
      this.stageHeight = this.$el.clientHeight;
    },
    postMessage({ web, app } = {}) {
      const getPostMessage = (payload) => {
        let message = { type: 'inistate' };

        if (Object.prototype.toString.call(payload) === "[object Object]") {
          message = { ...payload, ...message };
        }

        return JSON.stringify(message);
      }

      if (web) {
        const postMessage = getPostMessage(web);
        console.info(`From Device Preview to WEB`, postMessage);
        this.$refs['device-web-content'].contentWindow.postMessage(postMessage, "*");
      }

      if (app) {
        const postMessage = getPostMessage(app);
        console.info(`From Device Preview to APP`, postMessage);
        this.$refs['device-app-content'].contentWindow.postMessage(postMessage, "*");
      }
    },
    reload() {
      const webSrc = this.$refs['device-web-content'].src;
      const appSrc = this.$refs['device-app-content'].src;
      this.$refs['device-web-content'].src = "";
      this.$refs['device-app-content'].src = "";
      setTimeout(() => {
        this.$refs['device-web-content'].src = webSrc;
        this.$refs['device-app-content'].src = appSrc;
      }, 100);
    }
  },
  created() {
    this.Web = "web";
    this.PhonePortrait = "phonePortrait";
    this.PhoneLandscape = "phoneLandscape";
    this.PadPortrait = "padPortrait";
    this.PadLandscape = "padLandscape";
  },
  mounted() {
    /* SS06049 (reopen): measure in the observer callback itself. ResizeObserver already delivers
       at most once per frame, after layout and before paint, so no throttle is needed -- and a
       debounce left the web iframe at the OLD stage size for its whole wait, overflowing a stage
       that had just shrunk (and the device scale lagging the same way). */
    this.resizeObserver = new ResizeObserver(() => this.measureStage());
    this.resizeObserver.observe(this.$el);
    this.measureStage();
    /* SS06049: fetch every bezel up front, so the first switch to a device draws its artwork
       at once instead of an empty (correctly sized) frame while the SVG loads */
    this.bezelPreloads = [this.iphone, this.iphoneLandscape, this.ipad, this.ipadLandscape].map(src => {
      const image = new Image();
      image.src = src;
      return image;
    });
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.unobserve(this.$el);
      this.resizeObserver = null;
    }
  }
}
</script>

<style lang="scss" scoped>

.fts-device {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  .device-app-container {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    .device-app-content {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      &.device-app-content--app {
        border-radius: 2.75rem;
      }
    }
    .device-app-frame {
      pointer-events: none;
      z-index: 1;
    }
  }
  &.device--web {
    border-top: 1px solid var(--fts-border);
    border-radius: 0.375rem;
    .device-web-content {
      border: 1px solid var(--fts-border);
      border-radius: 0.375rem;
    }
  }
}
</style>